<template>
    <main class="p-2 w-full h-full max-h-main flex flex-col items-start min-h-main">
        <div class="w-full">
            <div class="flex w-full gap-2 items-center">
                <app-button @click="addInvoice()" :label="$t('invoices.newInvoice')" v-if="!readOnly" />

                <span class="flex-grow"></span>
                <a
                    :href="lastInvoicesExport ? lastInvoicesExport.url : '#'"
                    ref="lastPdfLink"
                    download
                    target="_blank"
                    class="underline text-xs"
                    v-if="lastInvoicesExport && lastInvoicesExport.generationSucceeded"
                    :title="$t('commons.lastExport') + ' (' + humanizeDate(lastInvoicesExport.createdAt) + ')'"
                >
                    {{ $t('commons.lastExport') }}
                </a>
                <app-button
                    size="mini"
                    class="p-2 shadow"
                    v-click-helper="openPdf"
                    :aria-label="$t('commons.toPdf')"
                    :title="$t('commons.toPdf')"
                    :disabled="lastInvoicesExport && !lastInvoicesExport.generationEndDate"
                    :icon="
                        lastInvoicesExport && !lastInvoicesExport.generationEndDate
                            ? 'icon-rotate-right'
                            : 'icon-file-pdf-outline'
                    "
                    :icon-class="
                        lastInvoicesExport && !lastInvoicesExport.generationEndDate ? 'animate animate-spin' : ''
                    "
                />
            </div>
            <app-multi-picker
                ref="filter"
                icon="icon-magnify"
                :allowStringCriteria="true"
                class="w-full my-2"
                v-model="filterValue"
                :options="filterOptions"
                :strictMatching="true"
            >
                <template v-slot:option="{ option }">
                    <span>{{ option.fullName || option.name }}</span>
                    <span class="text-xs text-gray-600 ml-1">{{ option.criteriaType }}</span>
                </template>
            </app-multi-picker>
            <div class="flex justify-between">
                <div>
                    <app-select @input="onAction" v-if="!loading && selection.length > 0 && !readOnly" class="text-xs">
                        <option value="" disabled selected>{{ $t('commons.actions') }}</option>
                        <option value="modify">{{ $t('commons.actionModify') }}</option>
                        <option value="delete">{{ $t('commons.actionDelete') }}</option>
                    </app-select>
                </div>
            </div>
        </div>
        <div class="flex w-full flex-grow overflow-auto flex-col text-xs relative">
            <div v-if="loading" class="flex justify-center">
                <icon-rotate-right class="animate animate-spin"></icon-rotate-right>
            </div>
            <table class="table-fixed" v-else>
                <thead class="sticky top-0 bg-white">
                    <tr>
                        <th style="width: 2rem" class="hidden sm:table-cell text-left border-r p-1" v-if="!readOnly">
                            <app-checkbox
                                :value="selection.length === filteredItems.length && selection.length > 0"
                                :indeterminate="selection.length > 0 && selection.length < filteredItems.length"
                                :show-label="false"
                                :label="$t('commons.toggleSelectAll')"
                                @input="toggleSelectAll"
                            ></app-checkbox>
                        </th>
                        <th class="text-left border-r p-1" style="width: 15rem">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('emitter')"
                            >
                                {{ $t('commons.bundle') }}
                                <div v-if="sortKey === 'emitter'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th class="text-left border-r p-1">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('object')"
                            >
                                {{ $t('commons.object') }}
                                <div v-if="sortKey === 'object'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th style="width: 3rem" class="hidden sm:table-cell text-center border-r p-1">
                            <div :title="$t('commons.version')">
                                {{ $t('commons.versionShort') }}
                            </div>
                        </th>
                        <th class="text-center border-r p-1" style="width: 6rem">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('emissionDate')"
                            >
                                {{ $t('invoices.emissionDateShort') }}
                                <div v-if="sortKey === 'emissionDate'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th class="text-center border-r p-1" style="width: 9rem">
                            <div class="flex flex-col">
                                <span>{{ $t('commons.amountHT') }}</span>
                                ({{ $n(filteredTotalHT, currencyFormat) }})
                            </div>
                        </th>
                        <th class="text-center border-r p-1" colspan="2" style="width: 9rem">
                            <div class="flex flex-col">
                                <span>{{ $t('commons.amountTTC') }}</span>
                                ({{ $n(filteredTotalTTC, currencyFormat) }})
                            </div>
                        </th>
                        <th style="width: 6rem" class="hidden sm:table-cell text-center border-r p-1">
                            <div>
                                {{ $t('invoices.invoiceStatus') }}
                            </div>
                        </th>
                        <th style="width: 6rem" class="hidden sm:table-cell text-center border-r p-1">
                            <div>
                                {{ $t('invoices.situationStatusTitle') }}
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="invoice in filteredItems">
                        <tr class="odd:bg-blue-50 border-t">
                            <td class="hidden sm:table-cell border-r p-1" v-if="!readOnly">
                                <a :id="'uuid_' + invoice.id" style="scroll-margin-top: 3em"></a>
                                <app-checkbox
                                    :value="invoice.isSelected"
                                    :label="$t('commons.select')"
                                    :show-label="false"
                                    @input="saveSelection(invoice)"
                                ></app-checkbox>
                            </td>
                            <td class="text-left border-r p-1">
                                <div
                                    :title="
                                        invoice.emitter && invoice.emitter.company ? invoice.emitter.company.name : ''
                                    "
                                >
                                    {{ invoice.emitter ? invoice.emitter.label : '' }}
                                </div>
                            </td>
                            <td class="border-r p-1">
                                <router-link
                                    class="hover:underline"
                                    :to="{
                                        name: 'followInvoice',
                                        params: {
                                            ...$route.params,
                                            invoiceId: invoice.id,
                                        },
                                    }"
                                >
                                    <span class="font-bold">{{ invoice.object }}</span>
                                    <span v-if="invoice.code" class="mx-1">#{{ invoice.code }}</span>
                                    <span v-if="!invoice.object || invoice.object.trim().length === 0">...</span>
                                </router-link>
                            </td>
                            <td class="hidden sm:table-cell text-center border-r p-1">
                                <router-link
                                    class="hover:underline"
                                    :to="{
                                        name: 'followInvoice',
                                        params: {
                                            ...$route.params,
                                            invoiceId: invoice.id,
                                        },
                                    }"
                                >
                                    {{ invoice.index }}
                                </router-link>
                            </td>
                            <td class="hidden sm:table-cell text-center border-r p-1">
                                <app-date-link
                                    @enter="focusToNextDate(invoice)"
                                    :ref="'date_' + invoice.id"
                                    :disabled="readOnly"
                                    :label="$t('invoices.emissionDateShort')"
                                    :show-label="false"
                                    v-model="invoice.emissionDate"
                                    @input="updateDate(invoice, $event)"
                                ></app-date-link>
                            </td>
                            <td class="border-r p-1" style="width: 10rem">
                                <app-number-link
                                    v-model="invoice.amountHT"
                                    format="double"
                                    :positiveOnly="false"
                                    @input="saveAmount(invoice, $event)"
                                >
                                    <u class="text-right w-full">
                                        {{ invoice.amountHT ? $n(invoice.amountHT, currencyFormat) : '-' }}
                                    </u>
                                </app-number-link>
                            </td>
                            <td class="border-r p-1" style="width: 10rem">
                                <app-number-link
                                    v-model="invoice.amountTTC"
                                    format="double"
                                    :positiveOnly="false"
                                    @input="saveAmount(invoice, $event)"
                                >
                                    <u class="text-right w-full">
                                        {{ invoice.amountTTC ? $n(invoice.amountTTC, currencyFormat) : '-' }}
                                    </u>
                                </app-number-link>
                            </td>
                            <td class="border-r p-1" style="width: 20px">
                                <span
                                    :title="
                                        invoice.distribution?.length > 1 ? $t('invoices.distributedAmountTips') : ''
                                    "
                                >
                                    <icon-arrow-decision-outline
                                        width="16px"
                                        heigth="16px"
                                        :class="{ 'opacity-0': invoice.distribution?.length <= 1 }"
                                    />
                                </span>
                            </td>
                            <td class="text-left border-r p-1" :class="invoice.invoiceStatusColor">
                                {{ $t('invoices.status.' + invoice.invoiceStatus) }}
                            </td>
                            <td class="text-left border-r p-1" :class="invoice.situationStatusColor">
                                {{ $t('invoices.situationStatus.' + invoice.situationStatus) }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <app-quick-actions :options="quickActions" @choose="$event.run()"></app-quick-actions>
        <app-popup ref="modifyAllPopup" :showHeader="true" :title="$t('commons.actionOnSelection')">
            <ValidationObserver v-slot="{ invalid }" tag="form" ref="observer" class="p-2 gap-2 flex flex-col">
                <app-bundle-picker v-model="editedInvoice.emitter" :options="bundles"></app-bundle-picker>
                <app-date-input v-model="editedInvoice.emissionDate" :label="$t('invoices.emissionDate')" />
                <app-date-input v-model="editedInvoice.emissionDate" :label="$t('invoices.conclusion')" />
                <app-select v-model="editedInvoice.conclusion">
                    <option value="validated">{{ $t('invoices.validated') }}</option>
                    <option value="rejected">{{ $t('invoices.rejected') }}</option>
                    <option value="toValidate">{{ $t('invoices.toValidate') }}</option>
                </app-select>
                <app-footer @click="saveMultiple" :disabled="invalid" class="mt-2"></app-footer>
            </ValidationObserver>
        </app-popup>
    </main>
</template>

<script>
import AppCheckbox from '../../components/app-checkbox/AppCheckbox';
import { isMobile } from '@/state/state';
import { filterMatch, get, getMapById, sortBy, uniqBy } from '@/services/sanitize.service';
import AppList from '../../components/appList/AppList';
import AppSelect from '@/components/appSelect/AppSelect';
import AppMultiPicker from '@/components/appMultiPicker/AppMultiPicker';
import AppQuickActions from '@/components/appQuickActions/AppQuickActions';
import AppButton from '@/components/appButton/AppButton';
import {
    createInvoice,
    getInvoices,
    getInvoiceStatus,
    getSituationStatus,
    removeInvoiceGroup,
    updateInvoice,
} from '@/features/invoices/invoices.service';
import AppPreparationVisaStatus from '@/components/appPreparationVisaStatus/AppPreparationVisaStatus';
import AppDateLink from '@/components/appDateLink/AppDateLink';
import { confirm } from '@/features/dialogs/dialogs.service';
import AppPopup from '@/components/app-popup/AppPopup';
import AppBundlePicker from '@/components/appBundlePicker';
import AppDateInput from '@/components/appDateInput/AppDateInput';
import AppFooter from '@/components/appFooter/AppFooter';
import AppInputText from '@/components/appInputText/AppInputText';
import AppCancel from '@/components/appCancel/AppCancel';
import IconMenuDown from '@/icons/IconMenuDown';
import IconMenuUp from '@/icons/IconMenuUp';
import AppNumberInput from '@/components/appNumberInput/AppNumberInput';
import AppNumberLink from '@/components/appNumberLink/AppNumberLink';
import { queryProject } from '@/features/projects/projects.service';

import { combineLatest } from 'rxjs';
import { getBundles } from '@/features/bundles/bundles.service';
import { getCompanies } from '@/features/companies/companies.service';
import AppTips from '@/components/app-tips/AppTips.vue';
import AppTextarea from '@/components/app-textarea/AppTextarea.vue';
import AppLabel from '@/components/appLabel/AppLabel.vue';
import IconAlertCircle from '@/icons/IconAlertCircle.vue';
import IconAlertOutline from '@/icons/IconAlertOutline.vue';
import { replicateProjects } from '@/rxdb/database';
import { error, info } from '@/features/toasts/toats.service';
import { getLastInvoicesExport, createInvoicesExport } from '@/features/invoices/invoicesExports.service';
import { humanizeDate } from '@/filters/dateFilter';
import vueClickHelper from 'vue-click-helper';
import { getInvoicesReviews } from '@/features/invoices/invoiceReviews.service';

export default {
    components: {
        IconAlertOutline,
        IconAlertCircle,
        AppLabel,
        AppTextarea,
        AppTips,
        AppNumberLink,
        AppNumberInput,
        IconMenuUp,
        IconMenuDown,
        AppCancel,
        AppInputText,
        AppFooter,
        AppDateInput,
        AppBundlePicker,
        AppPopup,
        AppDateLink,
        AppPreparationVisaStatus,
        AppButton,
        AppQuickActions,
        AppMultiPicker,
        AppSelect,
        AppList,
        AppCheckbox,
    },
    directives: { 'click-helper': vueClickHelper },
    async created() {
        this.restoreFilter();
        queryProject(this.$route.params.projectId).then((project) => {
            this.readOnly = !project.me.allowedFeatures.includes('project_invoices');
            this.currencyFormat.currency = project.currency;
        });
        this.init();
    },
    computed: {
        filteredTotalTTC() {
            return this.filteredItems.reduce((acc, invoice) => (invoice.amountTTC ? acc + invoice.amountTTC : acc), 0);
        },
        filteredTotalHT() {
            return this.filteredItems.reduce((acc, invoice) => (invoice.amountHT ? acc + invoice.amountHT : acc), 0);
        },
        filteredItems() {
            let result = uniqBy(this.filterFn(this.filterValue), 'groupId').map((item) => ({
                ...item,
                isSelected: this.selection.includes(item.id),
            }));
            if (this.sortKey) {
                result = sortBy(result, (item) => {
                    const text = item.code ? item.code + ' ' + item.object : item.object;
                    if (this.sortKey === 'object') {
                        return text;
                    } else if (this.sortKey === 'emissionDate') {
                        return item.emissionDate + ' ' + text;
                    } else if (this.sortKey === 'emitter') {
                        return (item.bundle ? item.bundle.label : null) + text;
                    }
                });
                if (!this.sortAsc) {
                    result.reverse();
                }
            }
            return result;
        },
        filterOptions() {
            return [
                {
                    isGroup: true,
                    name: this.$t('commons.status'),
                    id: this.$t('commons.status'),
                    children: [
                        {
                            id: 'invoice_toEmit',
                            name: this.$t('invoices.status.toEmit'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'invoice_toValidate',
                            name: this.$t('invoices.status.toValidate'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'invoice_validated',
                            name: this.$t('invoices.status.validated'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'invoice_rejected',
                            name: this.$t('invoices.status.rejected'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                        {
                            id: 'invoice_obsolete',
                            name: this.$t('invoices.status.obsolete'),
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                    ],
                },
                {
                    isGroup: true,
                    name: this.$t('commons.emitterCriteriaType'),
                    id: this.$t('commons.emitterCriteriaType'),
                    children: this.bundles.map((bundle, index) => ({
                        ...bundle,
                        name: bundle.label,
                        firstOfCriteriaType: index === 0,
                        criteriaType: this.$t('commons.emitterCriteriaType'),
                        _isBundleCriteria: true,
                    })),
                },
            ];
        },
    },
    methods: {
        humanizeDate,
        init() {
            this.subscriptions = [
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getInvoices(this.$route.params.projectId),
                    getCompanies(this.$route.params.projectId),
                    getInvoicesReviews(this.$route.params.projectId),
                ]).subscribe(async ([bundles, invoices, companies, invoiceReviews]) => {
                    this.companies = companies.map((company) => ({
                        ...company,
                        bundleIds: bundles
                            .filter((bundle) => bundle.companyId === company.id)
                            .map((bundle) => bundle.id),
                    }));
                    const companyMap = getMapById(companies);
                    this.bundles = sortBy(
                        bundles.map((bundle) => ({ ...bundle, company: companyMap[bundle.companyId] })),
                        (bundle) => bundle.label,
                    );
                    this.bundleMap = getMapById(this.bundles);
                    this.restoreSelection();
                    this.items = uniqBy(invoices, 'groupId').map((invoice) => {
                        const emitter = this.bundleMap[invoice.emitterId];
                        const invoiceStatus = getInvoiceStatus(
                            invoice,
                            invoiceReviews.filter((invoiceReview) => invoiceReview.invoiceId === invoice.id),
                        );
                        const situationStatus = getSituationStatus(
                            invoice,
                            invoiceReviews.filter((invoiceReview) => invoiceReview.invoiceId === invoice.id),
                        );
                        return {
                            ...invoice,
                            index: invoice.versions.length,
                            versions: invoice.versions.map((version) => ({
                                ...version,
                                emitter: this.bundleMap[version.bundleId],
                            })),
                            emitter,
                            filterString: [
                                invoice.code,
                                invoice.object,
                                invoice.emitterId && emitter ? emitter.label : '',
                            ].join(),
                            isSelected: this.selection.includes(invoice.id),
                            invoiceStatusColor: this.invoiceStatusColors[invoiceStatus] || '',
                            invoiceStatus,
                            situationStatus,
                            situationStatusColor: this.situationStatusColors[situationStatus] || '',
                        };
                    });
                    this.cleanupSavedSelection();
                    this.scrollToLastVisited();
                    this.lastInvoicesExport = await getLastInvoicesExport(this.$route.params.projectId);
                    this.loading = false;
                }),
            ];
        },
        scrollToLastVisited() {
            const lastVisitedInvoiceId = localStorage.getItem(
                'invoice.lastVisitedInvoiceId.' + this.$route.params.projectId,
            );
            if (lastVisitedInvoiceId) {
                setTimeout(() => {
                    const element = this.$el.querySelector('#uuid_' + lastVisitedInvoiceId);
                    if (element) element.scrollIntoView();
                }, 800);
            }
        },
        get,
        sortBy(key) {
            if (key === this.sortKey) {
                this.sortAsc = !this.sortAsc;
            } else {
                this.sortKey = key;
                this.sortAsc = true;
            }
            this.saveFilter(this.filterValue);
        },
        saveMultiple() {
            this.selection.map((itemId) => {
                const selectedItems = this.items.filter((item) => this.selection.includes(item.id));
                const patch = {};
                if (this.editedInvoice.emissionDate) {
                    patch.emissionDate = this.editedInvoice.emissionDate;
                } else if (this.getSelectionCommonEmissionDate(selectedItems)) {
                    patch.emissionDate = null;
                }
                if (this.editedInvoice.conclusion) {
                    patch.conclusion = this.editedInvoice.conclusion;
                }
                return updateInvoice(this.$route.params.projectId, { id: itemId, ...patch });
            });
            this.$refs.modifyAllPopup.close();
        },
        async onAction(action) {
            const selectedItems = this.items.filter((item) => this.selection.includes(item.id));
            if (action === 'delete') {
                if (await confirm(this.$t('commons.confirmMessageAll', { number: this.selection.length }))) {
                    await Promise.all(
                        selectedItems.map((item) => removeInvoiceGroup(this.$route.params.projectId, item.groupId)),
                    );
                    this.selection = [];
                }
            } else if (action === 'modify') {
                this.editedInvoice = {
                    emissionDate: this.getSelectionCommonEmissionDate(selectedItems),
                    emitter: this.getSelectionCommonBundle(selectedItems),
                    conclusion: this.getSelectionCommonConclusion(selectedItems),
                };
                this.$refs.modifyAllPopup.open();
            }
        },
        getSelectionCommonBundle(selectedItems) {
            const firstItem = selectedItems.find((item) => !!item.bundle);
            if (firstItem && selectedItems.every((item) => item.bundle && item.bundle.id === firstItem.bundle.id)) {
                return firstItem.bundle;
            } else {
                return null;
            }
        },
        getSelectionCommonConclusion(selectedItems) {
            const firstItem = selectedItems.find((item) => !!item.conclusion);
            if (firstItem && selectedItems.every((item) => item.conclusion === firstItem.conclusion)) {
                return firstItem.conclusion;
            } else {
                return null;
            }
        },
        getSelectionCommonEmissionDate(selectedItems) {
            const firstItem = selectedItems.find((item) => !!item.emissionDate);
            if (
                firstItem &&
                selectedItems.every(
                    (item) => item.emissionDate && item.emissionDate.getTime() === firstItem.emissionDate.getTime(),
                )
            ) {
                return firstItem.emissionDate;
            } else {
                return null;
            }
        },
        toggleSelectAll() {
            if (this.selection.length < this.filteredItems.length) {
                this.selection = this.filteredItems.map((item) => item.id);
            } else {
                this.selection = [];
            }
            this.saveSelection();
        },
        cleanupSavedSelection() {
            this.selection = this.selection.filter((itemId) => !!this.items.find((anItem) => anItem.id === itemId));
        },
        saveSelection(item) {
            if (item) {
                if (!item.isSelected) {
                    this.selection.push(item.id);
                } else {
                    this.selection = this.selection.filter((anItem) => anItem !== item.id);
                }
            }
            localStorage.setItem('invoices.selection.' + this.$route.params.projectId, JSON.stringify(this.selection));
        },
        restoreSelection() {
            const cache = localStorage.getItem('invoices.selection.' + this.$route.params.projectId);
            if (cache) {
                this.selection = JSON.parse(cache);
            }
        },
        async addInvoice() {
            const result = await createInvoice(this.$route.params.projectId, {
                object: '',
            });
            await this.$router.push({
                name: 'followInvoice',
                params: {
                    projectId: this.$route.params.projectId,
                    invoiceId: result.id,
                },
            });
        },
        matchBundleFilter(item, bundleIds) {
            return item.emitter && bundleIds.includes(item.emitter.id);
        },
        matchString(stringCriteria, item) {
            if (!stringCriteria || stringCriteria.length === 0) {
                return true;
            }
            return stringCriteria.find((criteria) => filterMatch(item.filterString || item.name, criteria, true));
        },
        matchState(stateCriteria, item) {
            if (!stateCriteria || stateCriteria.length === 0) {
                return true;
            }
            return stateCriteria.find(
                (criteria) => item.invoiceStatus && criteria.id === 'invoice_' + item.invoiceStatus,
            );
        },
        saveFilter(filterValue) {
            localStorage.setItem(
                'invoices_filter_' + this.$route.params.projectId,
                JSON.stringify({
                    filterValue,
                    sortKey: this.sortKey,
                }),
            );
        },
        restoreFilter() {
            const cache = localStorage.getItem('invoices_filter_' + this.$route.params.projectId);
            if (cache) {
                const parsedCache = JSON.parse(cache);
                this.filterValue = parsedCache.filterValue || [];
                this.sortKey = parsedCache.sortKey || 'object';
            }
        },
        filterFn(filter) {
            this.saveFilter(filter);
            const bundleIds = [...filter.filter((aCriteria) => aCriteria._isBundleCriteria).map((bundle) => bundle.id)];
            const stringCriteria = filter
                .filter((aCriteria) => aCriteria._isStringCriteria)
                .map((aCriteria) => aCriteria.content);
            const stateCriteria = filter.filter((aCriteria) => aCriteria._isStateCriteria);
            return this.items.filter((item) => {
                const fullCriteria = {
                    matchBundleFilter: bundleIds.length === 0 || this.matchBundleFilter(item, bundleIds),
                    matchString: this.matchString(stringCriteria, item),
                    matchState: this.matchState(stateCriteria, item),
                };
                const filterResult = Object.values(fullCriteria).every((value) => !!value);
                if (!filterResult) {
                    this.selection = this.selection.filter((id) => id !== item.id);
                }
                return filterResult;
            });
        },
        updateDate(invoice, date) {
            updateInvoice(this.$route.params.projectId, {
                id: invoice.id,
                emissionDate: date,
            });
        },
        saveAmount(invoice, amountTTC) {
            updateInvoice(this.$route.params.projectId, {
                id: invoice.id,
                amountTTC,
                distribution: invoice.emitterId ? [{ id: invoice.emitterId, amountTTC }] : [],
            });
        },
        focusToNextDate(invoice) {
            let index = this.filteredItems.findIndex((aInvoice) => aInvoice.id === invoice.id);
            let nextInvoice = this.filteredItems[index + 1];
            while (!nextInvoice && index < this.filteredItems.length) {
                nextInvoice = this.filteredItems[++index];
            }
            if (nextInvoice) {
                this.$emit('select', nextInvoice);
                const element = this.$refs['date_' + nextInvoice.id];
                if (element) {
                    if (Array.isArray(element)) {
                        element[0].focus();
                    } else {
                        element.focus();
                    }
                }
            }
        },
        openPdf(e, isDoubleClick) {
            if (isDoubleClick) {
                this.$refs.lastPdfLink.click();
            } else {
                this.exportPDF();
            }
        },
        async exportPDF() {
            await replicateProjects(this.$route.params.projectId);
            this.lastInvoicesExport = { generationEndDate: null };
            const lastInvoicesExport = await createInvoicesExport(this.$route.params.projectId, {
                sortKey: this.sortKey,
                sortAsc: this.sortAsc,
                filter: this.filterValue,
            });
            if (lastInvoicesExport) {
                if (lastInvoicesExport.generationSucceeded === true) {
                    info(
                        this.$t('commons.generationSucceed') +
                            `<br/><a href="${lastInvoicesExport.url}"><u>Télécharger le pdf</u></a>`,
                        5000,
                    );
                } else if (lastInvoicesExport.generationSucceeded === false) {
                    error(this.$t('commons.generationFailed'));
                }
            }
            this.lastInvoicesExport = lastInvoicesExport;
        },
    },
    data() {
        return {
            lastInvoicesExport: null,
            invoiceStatusColors: {
                toEmit: 'bg-amber-500',
                validated: 'bg-green-600',
                rejected: 'text-white bg-red-600',
            },
            situationStatusColors: {
                waitingForConclusion: '',
                toEmit: 'bg-amber-500',
                emitted: 'bg-green-600',
            },
            currencyFormat: {
                currency: 'EUR',
                currencyDisplay: 'symbol',
                style: 'currency',
                minimumFractionDigits: 2,
            },
            bundleMap: {},
            loading: 0,
            quickActions: [
                {
                    name: this.$t('invoices.newInvoice'),
                    run: () => this.addInvoice(),
                },
            ],
            sortKey: 'object',
            sortAsc: true,
            readOnly: true,
            selection: [],
            editedInvoice: {
                emissionDate: null,
                emitter: null,
                conclusion: null,
            },
            subscriptions: [],
            bundles: [],
            companies: [],
            isMobile,
            items: [],
            filterValue: [],
        };
    },
};
</script>
